import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dbad5jkdsci36477l6n0";

const feedbackTypes = ["提交新网址", "纠错 / 站点下线", "分类建议", "其他"];

export default function FeedbackForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [type, setType] = useState(feedbackTypes[0]);
  const [content, setContent] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honey = String(payload.get("website_alt") ?? "").trim();
    payload.delete("website_alt");

    if (honey) {
      setStatus("success");
      form.reset();
      setContent("");
      return;
    }

    payload.set("feedback_type", type);
    payload.set("content", content);

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setContent("");
        setType(feedbackTypes[0]);
        return;
      }

      setStatus("error");
      setFormError(serverMsg || `提交失败（状态码 ${response.status}），请稍后重试。`);
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请检查网络后重试。");
    }
  };

  return (
    <form
      id="feedback"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6"
    >
      <h2 className="font-heading font-bold text-lg text-foreground-950">提交网址 / 反馈建议</h2>
      <p className="mt-1.5 text-xs text-foreground-500 leading-5">
        想推荐的实用网站、发现的失效链接，或对分类体系的想法，都可以写在这里。我们会人工审核后处理。
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label htmlFor="site_name" className="block text-xs text-foreground-600 mb-1.5">
            网站名称 <span className="text-accent-500">*</span>
          </label>
          <input
            id="site_name"
            name="site_name"
            type="text"
            required
            maxLength={60}
            placeholder="例如：某某学习平台"
            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <label htmlFor="site_url" className="block text-xs text-foreground-600 mb-1.5">
            网站地址
          </label>
          <input
            id="site_url"
            name="site_url"
            type="text"
            maxLength={200}
            placeholder="https://example.com"
            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-xs text-foreground-600 mb-1.5">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            maxLength={80}
            placeholder="方便我们回复处理结果"
            className="w-full h-10 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <span className="block text-xs text-foreground-600 mb-1.5">反馈类型</span>
          <div className="flex flex-wrap gap-1.5">
            {feedbackTypes.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setType(item)}
                className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  type === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:text-primary-600"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="content" className="block text-xs text-foreground-600 mb-1.5">
            具体说明 <span className="text-accent-500">*</span>
          </label>
          <textarea
            id="content"
            name="content"
            required
            maxLength={500}
            rows={4}
            value={content}
            onChange={(event) => setContent(event.target.value.slice(0, 500))}
            placeholder="简单介绍这个网站的用途、适合人群，或描述你遇到的问题（500 字以内）"
            className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
          />
          <p className="mt-1 text-right text-[11px] text-foreground-400">
            {content.length}/500
          </p>
        </div>

        <div className="form-field-alt" aria-hidden="true">
          <label htmlFor="website_alt">请勿填写此项</label>
          <input
            id="website_alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>

        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full h-10 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? "提交中..." : "提交反馈"}
        </button>

        {status === "success" && (
          <p className="text-xs text-primary-700 bg-primary-50 border border-primary-100 rounded-md px-3 py-2 leading-5">
            提交成功，感谢你的反馈。编辑会在审核后更新网址库。
          </p>
        )}
        {status === "error" && (
          <p className="text-xs text-accent-800 bg-accent-50 border border-accent-100 rounded-md px-3 py-2 leading-5 break-all">
            提交失败：{formError || "请稍后重试。"}
          </p>
        )}
        <p className="text-[11px] text-foreground-400 leading-5">
          提交内容仅用于网址审核与站点改进，不会用于其他用途。
        </p>
      </div>
    </form>
  );
}